import React, { useEffect, useRef, useState } from "react";
import { motion } from 'framer-motion';
import { Fade } from "react-awesome-reveal";
import { MdChevronRight } from "react-icons/md";

import "../../styling.css";
import BackgroundCover from "../../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../../components/TitleWithDescription/TitleWithDescription";
import { debounce, modifyStyles, resetDefaultStyles } from "../../../utils/utilFunctions";
import { isMobile } from "../../../utils/appUtils";
import { COMPONENT_COLORS } from '../../../utils/componentColors';
import Title from "../../../components/Title/Title";
import Collection from "../../../components/Collection/Collection";
import { useLanguage } from "../../../context/LanguageContext";
import { getFluoringTranslations, FluoringTranslations } from "../../../translations/fluoringTemplate";
import { useEditMode } from "../../../context/EditModeContext";
import EditableText from "../../../components/EditableText/EditableText";
import { usePageContent } from "../../../hooks/usePageContent";
import { getLocalizedJewelleryCollectionItems } from "../../../utils/jewelleryCollectionUtils";

function FluoringCollection() {
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<FluoringTranslations>('fluoring-collection');

    // Use translations as fallback content
    const t = content || getFluoringTranslations(language);

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    const renderContent = () => {
        const collectionItemsForGrid = getLocalizedJewelleryCollectionItems('fluoring', language);

        return (
            <>
                {/* Fluorescent Initials */}
                <div className="py-4 md:py-8">
                    <TitleWithDescription
                        title={{
                            firstLine: isEditMode ? (
                                <EditableText
                                    contentKey="fluoring-collection.fluorTitle"
                                    defaultValue={t.fluorTitle}
                                />
                            ) : t.fluorTitle,
                            secondLine: isEditMode ? (
                                <EditableText
                                    contentKey="fluoring-collection.fluorSubtitle"
                                    defaultValue={t.fluorSubtitle}
                                />
                            ) : t.fluorSubtitle
                        }}
                        description={isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.fluorDescription"
                                defaultValue={t.fluorDescription}
                                multiline={true}
                            />
                        ) : t.fluorDescription}
                        layout="row"
                        className="px-4 md:px-8 lg:px-24"
                    />
                </div>

                {/* Video Section */}
                <div className="w-full relative aspect-video">
                    <video
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                    >
                        <source src="/video/fluoring.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </div>

                {/* Message from Inside */}
                <TitleWithDescription
                    title={{
                        firstLine: isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.messageTitle"
                                defaultValue={t.messageTitle}
                            />
                        ) : t.messageTitle,
                        secondLine: isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.messageSubtitle"
                                defaultValue={t.messageSubtitle}
                            />
                        ) : t.messageSubtitle
                    }}
                    description={isEditMode ? (
                        <EditableText
                            contentKey="fluoring-collection.messageDescription"
                            defaultValue={t.messageDescription}
                            multiline={true}
                        />
                    ) : t.messageDescription}
                    layout="column"
                    image={{
                        src: "/images/jewellery_fluoring_grid_3_maxi.png",
                        alt: "Fluoring jewelry piece",
                        position: "left",
                        className: "w-full md:w-auto md:max-w-[90%] h-auto"
                    }}
                    className="bg-gradient-to-b from-[#C4C2C3] via-[#C8C6C7] via-[#CECCCD] to-[#D1CFD0] px-4 md:px-8 lg:px-24"
                />

                {/* The Collection intro */}
                <TitleWithDescription
                    title={{
                        firstLine: isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.eternityTitle"
                                defaultValue={t.eternityTitle}
                            />
                        ) : t.eternityTitle,
                        secondLine: isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.eternitySubtitle"
                                defaultValue={t.eternitySubtitle}
                            />
                        ) : t.eternitySubtitle
                    }}
                    description={isEditMode ? (
                        <EditableText
                            contentKey="fluoring-collection.eternityDescription"
                            defaultValue={t.eternityDescription}
                            multiline={true}
                        />
                    ) : t.eternityDescription}
                    layout="row"
                    className="px-4 md:px-8 lg:px-24"
                />

                {/* The Collection */}
                <div className="pb-8 md:pb-16">
                    <Collection
                        items={collectionItemsForGrid}
                        collectionId="fluoring"
                        buildItemLink={(item) => `/jewellery/collections/fluoring/${item.id}`}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="min-h-screen bg-white">
            <BackgroundCover
                backgroundHeight={isMobile() ? 400 : undefined}
                imagePosition="center"
                backgroundImage="/images/jewellery_fluoring_background.jpg"
                isWatchPage={true}
            >
                <div className="absolute top-1/2 left-[100px] -translate-y-[200px] z-10 hidden md:block">
                    <Title
                        firstLine={isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.mainTitle"
                                defaultValue={t.mainTitle}
                            />
                        ) : t.mainTitle}
                        secondLine={isEditMode ? (
                            <EditableText
                                contentKey="fluoring-collection.mainSubtitle"
                                defaultValue={t.mainSubtitle}
                            />
                        ) : t.mainSubtitle}
                        className="text-white mt-[420px] hidden md:block"
                        animate={true}
                    />
                </div>
            </BackgroundCover>
            <div className="w-full">
                {renderContent()}
            </div>
        </div>
    );
}

export default FluoringCollection;
